import React, { useState, useEffect } from "react";
import {
  Box, Typography, TextField, Button, Drawer, IconButton,
  MenuItem, RadioGroup, FormControlLabel, Radio,
  Avatar, AvatarGroup, InputAdornment, CircularProgress,
  Dialog, DialogTitle, DialogContent, DialogActions,
  Divider, FormHelperText,
} from "@mui/material";
import { DatePicker, TimePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import CloseIcon from "@mui/icons-material/Close";
import SearchIcon from "@mui/icons-material/Search";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";
import ImageUploader from "../../common/imguploader/ImageUploader";
import Paginations from "../../common/pagination/Paginations";
import UserImg from "../../common/assests/sampleuser.svg";
import { allUserListApi } from "../../api";
import { useSelector } from "react-redux";
import dayjs from "dayjs";
import configuration from "../../config/Config";

const inputSx = {
  "& .MuiOutlinedInput-root": {
    borderRadius: "8px",
    background: "#fff",
    fontFamily: "Heebo",
    "& fieldset": { borderColor: "#E7E7E7" },
    "&:hover fieldset": { borderColor: "#D7D7D7" },
    "&.Mui-focused fieldset": { borderColor: "#065F46" },
  },
  "& .MuiInputLabel-root": { fontFamily: "Heebo" },
  "& .MuiInputLabel-root.Mui-focused": { color: "#065F46" },
  "& .MuiFormHelperText-root": { fontFamily: "Heebo", marginLeft: "4px", fontSize: "11px" },
};

const radioSx = {
  "& .MuiRadio-root": { color: "#CBCBCB" },
  "& .MuiRadio-root.Mui-checked": { color: "#065F46" },
  "& .MuiFormControlLabel-label": { fontFamily: "Heebo", fontSize: "14px", color: "#362B24" },
};

const NotificationModal = ({ open, onClose, onSave, editingData }) => {
  const token = useSelector((s) => s?.user?.user?.token);

  const [userList, setUserList] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(0);
  const [loading, setLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [myTimeout, setMyTimeout] = useState();
  const [totalUser, setTotalUser] = useState(0);

  const [sendTo, setSendTo] = useState(1);
  const [notificationDate, setNotificationDate] = useState(null);
  const [time, setTime] = useState(null);
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [picture, setPicture] = useState(null);
  const [selectedType1, setSelectedType1] = useState("Notification");
  const [selectedType, setSelectedType] = useState("All Users");
  const [selectUserDialog, setSelectUserDialog] = useState(false);
  const [selectedIds, setSelectedIds] = useState([]);
  const [shouldFetch, setShouldFetch] = useState(false);
  const [emailContent, setEmailContent] = useState("");
  const [tried, setTried] = useState(false);

  useEffect(() => {
    if (editingData) {
      setSendTo(editingData.sendTo === "Users" ? 1 : 2);
      setTime(editingData?.notificationTime ? dayjs(`2000-01-01T${editingData.notificationTime}`) : null);
      setNotificationDate(editingData.notificationDate ? dayjs(editingData.notificationDate) : null);
      setTitle(editingData.title || "");
      setDescription(editingData.description || "");
      setPicture(editingData.notificationPicture || null);
      setSelectedType1(editingData.notificationType || "Notification");
      if (editingData.notificationType === "Email") {
        setTitle("");
        setDescription("");
        setEmailContent(editingData?.description || "");
      }
      setSelectedType(editingData.sendNotification || "All Users");
      setSelectedIds(editingData.recipients || []);
    } else {
      setSendTo(1);
      setNotificationDate(null);
      setTime(null);
      setTitle("");
      setDescription("");
      setPicture(null);
      setSelectedType1("Notification");
      setSelectedType("All Users");
      setSelectedIds([]);
      setEmailContent("");
    }
    setTried(false);
  }, [editingData, open]);

  const fetchUsers = async () => {
    try {
      setLoading(true);
      const response = await allUserListApi({ token, currentPage, type: sendTo, searchQuery });
      setTotalUser(response?.totalUsers);
      setUserList(response?.users || []);
      setCurrentPage(response?.currentPage);
      setTotalPages(response?.totalPages);
    } catch (err) {
      console.log("error", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (shouldFetch) fetchUsers();
  }, [shouldFetch, currentPage]);

  useEffect(() => {
    if (!shouldFetch) return;
    if (!searchQuery.trim()) { fetchUsers(); return; }
    clearTimeout(myTimeout);
    const t = setTimeout(() => fetchUsers(), 500);
    setMyTimeout(t);
    return () => clearTimeout(t);
  }, [searchQuery]);

  const handleSendToChange = (e) => {
    setSendTo(e.target.value === "User" ? 1 : 2);
  };

  const openUserDialog = () => {
    setSelectUserDialog(true);
    setShouldFetch(true);
    setCurrentPage(1);
    setTotalPages(0);
  };

  const closeUserDialog = () => {
    setSelectUserDialog(false);
    setShouldFetch(false);
  };

  const toggleUser = (id) => {
    setSelectedIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );
  };

  const handleAddUsers = () => {
    setSelectedIds(Array.from(new Set(selectedIds)));
    closeUserDialog();
  };

  const getPlainText = (html) => {
    const doc = new DOMParser().parseFromString(html, "text/html");
    return doc.body.textContent || "";
  };

  // Compute validation errors (only shown after first save attempt)
  const errors = tried
    ? {
        date: !notificationDate ? "Date is required" : "",
        time: !time || !dayjs(time).isValid() ? "Time is required" : "",
        title: selectedType1 === "Notification" && !title.trim() ? "Title is required" : "",
        description: selectedType1 === "Notification" && !description.trim() ? "Description is required" : "",
        emailContent: selectedType1 === "Email" && !getPlainText(emailContent).trim() ? "Email content is required" : "",
        recipients: selectedType === "Selected Users" && selectedIds.length === 0 ? "Please select at least one recipient" : "",
      }
    : {};

  const hasErrors = (e) =>
    e.date || e.time || e.title || e.description || e.emailContent || e.recipients;

  const handleSave = () => {
    setTried(true);
    const e = {
      date: !notificationDate ? "err" : "",
      time: !time || !dayjs(time).isValid() ? "err" : "",
      title: selectedType1 === "Notification" && !title.trim() ? "err" : "",
      description: selectedType1 === "Notification" && !description.trim() ? "err" : "",
      emailContent: selectedType1 === "Email" && !getPlainText(emailContent).trim() ? "err" : "",
      recipients: selectedType === "Selected Users" && selectedIds.length === 0 ? "err" : "",
    };
    if (hasErrors(e)) return;

    let finalTitle = title;
    let finalDescription = description;
    if (selectedType1 === "Email") {
      finalTitle = "Email Notification";
      finalDescription = getPlainText(emailContent);
    }
    onSave({
      sendTo,
      notificationDate,
      time: time ? dayjs(time).format("HH:mm:ss") : "",
      title: finalTitle,
      description: finalDescription,
      picture,
      selectedType1,
      selectedType,
      recipients: selectedIds,
    });
  };

  return (
    <>
      <Drawer
        anchor="right"
        open={open}
        onClose={onClose}
        PaperProps={{
          sx: {
            width: { xs: "100%", sm: 560 },
            background: "#fff",
            borderTopLeftRadius: { sm: "12px" },
            borderBottomLeftRadius: { sm: "12px" },
            overflow: "hidden",
            display: "flex",
            flexDirection: "column",
          },
        }}
      >
        {/* HEADER */}
        <Box sx={{ px: 3, py: 2.5, borderBottom: "1px solid #F1F1F1", display: "flex", alignItems: "flex-start", justifyContent: "space-between", flexShrink: 0 }}>
          <Box>
            <Typography sx={{ fontSize: "20px", fontWeight: 700, color: "#362B24", fontFamily: "Heebo", lineHeight: 1.3 }}>
              {editingData ? "Edit Notification" : "Create Notification"}
            </Typography>
            <Typography sx={{ fontSize: "13px", color: "#8B8B8B", mt: 0.4, fontFamily: "Heebo" }}>
              Manage push notifications and email campaigns
            </Typography>
          </Box>
          <IconButton onClick={onClose} sx={{ border: "1px solid #EBEBEB", width: 36, height: 36, borderRadius: "6px", flexShrink: 0, ml: 1 }}>
            <CloseIcon sx={{ fontSize: 18, color: "#362B24" }} />
          </IconButton>
        </Box>

        {/* BODY */}
        <Box sx={{ flex: 1, overflowY: "auto", px: 3, py: 3, display: "flex", flexDirection: "column", gap: 3 }}>

          {/* Cover image — push only */}
          {selectedType1 === "Notification" && (
            <Box>
              <Typography sx={{ mb: 1, fontSize: "13px", fontWeight: 600, color: "#362B24", fontFamily: "Heebo" }}>
                Notification Image
              </Typography>
              <Box sx={{ border: "1px dashed #E0E0E0", borderRadius: "8px", overflow: "hidden", background: "#FAFAFA" }}>
                <ImageUploader data="Upload Image" onUpload={(img) => setPicture(img)} setPicture={setPicture} picture={picture} />
              </Box>
            </Box>
          )}

          {/* Core fields grid */}
          <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" }, gap: 2 }}>
            <TextField
              select fullWidth size="small" label="Send To"
              value={sendTo === 1 ? "User" : "Sellers"}
              onChange={handleSendToChange}
              sx={inputSx}
            >
              <MenuItem value="User">Users</MenuItem>
              <MenuItem value="Sellers">Vendors</MenuItem>
            </TextField>

            <LocalizationProvider dateAdapter={AdapterDayjs}>
              <DatePicker
                disablePast
                value={notificationDate}
                onChange={(v) => setNotificationDate(v)}
                slotProps={{
                  textField: {
                    size: "small",
                    fullWidth: true,
                    label: "Send Date",
                    sx: inputSx,
                    error: !!errors.date,
                    helperText: errors.date || "",
                  },
                }}
              />
            </LocalizationProvider>

            <LocalizationProvider dateAdapter={AdapterDayjs}>
              <TimePicker
                value={time}
                onChange={(v) => setTime(v)}
                slotProps={{
                  textField: {
                    size: "small",
                    fullWidth: true,
                    label: "Send Time",
                    sx: inputSx,
                    error: !!errors.time,
                    helperText: errors.time || "",
                  },
                }}
              />
            </LocalizationProvider>

            <TextField
              select fullWidth size="small" label="Type"
              value={selectedType1}
              onChange={(e) => { setSelectedType1(e.target.value); setTried(false); }}
              sx={inputSx}
            >
              <MenuItem value="Notification">Push Notification</MenuItem>
              <MenuItem value="Email">Email</MenuItem>
            </TextField>
          </Box>

          {/* Content */}
          {selectedType1 === "Email" ? (
            <Box>
              <Typography sx={{ mb: 1, fontSize: "13px", fontWeight: 600, color: "#362B24", fontFamily: "Heebo" }}>
                Email Content
              </Typography>
              <Box
                sx={{
                  border: errors.emailContent ? "1px solid #EA001B" : "1px solid #E7E7E7",
                  borderRadius: "8px",
                  overflow: "hidden",
                  "& .ql-toolbar": { border: "none", borderBottom: "1px solid #EAEAEA" },
                  "& .ql-container": { border: "none", minHeight: "200px", fontFamily: "Heebo" },
                }}
              >
                <ReactQuill theme="snow" value={emailContent} onChange={setEmailContent} />
              </Box>
              {errors.emailContent && (
                <FormHelperText sx={{ color: "#EA001B", fontFamily: "Heebo", ml: "4px", fontSize: "11px", mt: "3px" }}>
                  {errors.emailContent}
                </FormHelperText>
              )}
            </Box>
          ) : (
            <>
              <TextField
                fullWidth size="small" label="Notification Title"
                placeholder="Enter notification title"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                error={!!errors.title}
                helperText={errors.title || `${title.length}/100`}
                inputProps={{ maxLength: 100 }}
                sx={inputSx}
              />
              <TextField
                fullWidth multiline rows={4} label="Description"
                placeholder="Enter notification message"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                error={!!errors.description}
                helperText={errors.description || `${description.length}/500`}
                inputProps={{ maxLength: 500 }}
                sx={{
                  ...inputSx,
                  "& .MuiOutlinedInput-root": { ...inputSx["& .MuiOutlinedInput-root"], alignItems: "flex-start" },
                }}
              />
            </>
          )}

          {/* Audience */}
          <Box>
            <Typography sx={{ mb: 1.5, fontSize: "13px", fontWeight: 600, color: "#362B24", fontFamily: "Heebo" }}>
              Audience
            </Typography>
            <Box sx={{ border: "1px solid #F0F0F0", borderRadius: "8px", px: 2, py: 1, background: "#FAFAFA" }}>
              <RadioGroup value={selectedType} onChange={(e) => { setSelectedType(e.target.value); setTried(false); }} sx={radioSx}>
                <FormControlLabel
                  value="All Users"
                  control={<Radio size="small" />}
                  label={`All ${sendTo === 1 ? "Users" : "Vendors"}`}
                />
                <FormControlLabel
                  value="Selected Users"
                  control={<Radio size="small" />}
                  label={`Selected ${sendTo === 1 ? "Users" : "Vendors"} only`}
                />
              </RadioGroup>
            </Box>
          </Box>

          {/* Selected recipients */}
          {selectedType === "Selected Users" && (
            <Box sx={{ border: errors.recipients ? "1px solid #EA001B" : "1px solid #ECECEC", borderRadius: "8px", p: 2.5, background: "#FAFAFA" }}>
              <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 2 }}>
                <Box>
                  <Typography sx={{ fontWeight: 600, color: "#362B24", fontFamily: "Heebo", fontSize: "14px" }}>
                    Recipients
                  </Typography>
                  <Typography sx={{ fontSize: "12px", color: selectedIds.length === 0 && errors.recipients ? "#EA001B" : "#8B8B8B", mt: 0.3, fontFamily: "Heebo" }}>
                    {selectedIds.length === 0 ? (errors.recipients || "No recipients selected") : `${selectedIds.length} selected`}
                  </Typography>
                </Box>
                <Button
                  onClick={openUserDialog}
                  sx={{
                    borderRadius: "8px", height: "36px", px: 2.5,
                    background: "linear-gradient(180deg, #065F46 0%, #0D7B5C 100%)",
                    color: "#fff", fontFamily: "Heebo", fontSize: "13px", textTransform: "none",
                    "&:hover": { opacity: 0.9, background: "linear-gradient(180deg, #065F46 0%, #0D7B5C 100%)" },
                  }}
                >
                  Add {sendTo === 1 ? "Users" : "Vendors"}
                </Button>
              </Box>
              {selectedIds.length > 0 && (
                <AvatarGroup max={6} sx={{ mt: 2, justifyContent: "flex-start" }}>
                  {userList.map((u, i) => (
                    <Avatar
                      key={i}
                      alt={u?.fullname}
                      src={u?.picture ? `${configuration.cdnFront}/${u.picture}` : UserImg}
                      sx={{ width: 32, height: 32 }}
                    />
                  ))}
                </AvatarGroup>
              )}
            </Box>
          )}
        </Box>

        {/* FOOTER */}
        <Box sx={{ px: 3, py: 2, borderTop: "1px solid #F1F1F1", display: "flex", justifyContent: "flex-end", gap: 1.5, flexShrink: 0, background: "#fff" }}>
          <Button
            onClick={onClose}
            sx={{ borderRadius: "8px", height: "41px", px: 3, border: "1px solid #EBEBEB", color: "#362B24", fontFamily: "Heebo", textTransform: "none", fontSize: "14px" }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleSave}
            disabled={loading}
            sx={{
              borderRadius: "8px", height: "41px", px: 4, minWidth: 160,
              background: "linear-gradient(180deg, #065F46 0%, #0D7B5C 100%)",
              color: "#fff", fontFamily: "Heebo", fontWeight: 600, textTransform: "none", fontSize: "14px",
              boxShadow: "1px 2px 30px 0px rgba(0,0,0,0.08)",
              "&:hover": { opacity: 0.9, background: "linear-gradient(180deg, #065F46 0%, #0D7B5C 100%)" },
              "&:disabled": { opacity: 0.5, color: "#fff" },
            }}
          >
            {loading
              ? <CircularProgress size={18} sx={{ color: "#fff" }} />
              : editingData ? "Update Notification" : "Create Notification"}
          </Button>
        </Box>
      </Drawer>

      {/* USER SELECTION DIALOG */}
      <Dialog
        open={selectUserDialog}
        onClose={closeUserDialog}
        maxWidth="xs"
        fullWidth
        PaperProps={{ sx: { borderRadius: "6px", overflow: "hidden" } }}
      >
        <DialogTitle sx={{ px: 3, pt: 2.5, pb: 1.5, display: "flex", alignItems: "center", justifyContent: "space-between" }}>
          <Typography sx={{ fontSize: "17px", fontWeight: 700, color: "#362B24", fontFamily: "Heebo" }}>
            Add {sendTo === 1 ? "Users" : "Vendors"}
          </Typography>
          <IconButton size="small" onClick={closeUserDialog} sx={{ border: "1px solid #EBEBEB", borderRadius: "8px" }}>
            <CloseIcon sx={{ fontSize: 16, color: "#362B24" }} />
          </IconButton>
        </DialogTitle>

        <Box sx={{ px: 3, pb: 1 }}>
          <TextField
            fullWidth size="small"
            placeholder={`Search ${sendTo === 1 ? "users" : "vendors"}...`}
            value={searchQuery}
            onChange={(e) => {
              if (e.target.value.trim() !== "" || e.target.value === "") setSearchQuery(e.target.value);
            }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon sx={{ fontSize: 18, color: "#8B8B8B" }} />
                </InputAdornment>
              ),
            }}
            sx={inputSx}
          />
        </Box>

        <DialogContent sx={{ px: 3, py: 1, maxHeight: 360, overflowY: "auto" }}>
          {loading ? (
            <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
              <CircularProgress size={24} sx={{ color: "#065F46" }} />
            </Box>
          ) : userList.length === 0 ? (
            <Typography sx={{ textAlign: "center", color: "#8B8B8B", fontFamily: "Heebo", py: 3, fontSize: "14px" }}>
              No {sendTo === 1 ? "users" : "vendors"} found
            </Typography>
          ) : (
            userList.map((user, i) => (
              <React.Fragment key={user._id}>
                {i > 0 && <Divider sx={{ borderColor: "#F4F4F4" }} />}
                <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", py: 1.5 }}>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, minWidth: 0 }}>
                    <Avatar
                      src={user?.picture ? `${configuration.cdnFront}/${user.picture}` : UserImg}
                      alt={user?.fullname || "User"}
                      sx={{ width: 36, height: 36, flexShrink: 0 }}
                    />
                    <Typography sx={{ fontFamily: "Heebo", color: "#362B24", fontSize: "14px", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                      {user?.fullname || "N/A"}
                    </Typography>
                  </Box>
                  <Button
                    size="small"
                    onClick={() => toggleUser(user._id)}
                    sx={{
                      borderRadius: "6px", height: "30px", px: 2,
                      background: selectedIds.includes(user._id) ? "rgba(234,0,27,0.08)" : "rgba(6,95,70,0.08)",
                      color: selectedIds.includes(user._id) ? "#EA001B" : "#065F46",
                      fontFamily: "Heebo", fontSize: "12px", fontWeight: 600, textTransform: "none",
                      flexShrink: 0, ml: 1,
                      "&:hover": {
                        background: selectedIds.includes(user._id) ? "rgba(234,0,27,0.14)" : "rgba(6,95,70,0.14)",
                      },
                    }}
                  >
                    {selectedIds.includes(user._id) ? "Remove" : "Select"}
                  </Button>
                </Box>
              </React.Fragment>
            ))
          )}

          {totalPages > 1 && (
            <Box sx={{ display: "flex", justifyContent: "center", pt: 1 }}>
              <Paginations totalPages={totalPages} currentPage={currentPage} onPageChange={setCurrentPage} disabled={loading} />
            </Box>
          )}
        </DialogContent>

        <DialogActions sx={{ px: 3, py: 2, borderTop: "1px solid #F1F1F1", gap: 1.5 }}>
          <Button
            onClick={closeUserDialog}
            sx={{ borderRadius: "8px", height: "38px", px: 3, border: "1px solid #EBEBEB", color: "#362B24", fontFamily: "Heebo", textTransform: "none", fontSize: "13px" }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleAddUsers}
            sx={{
              borderRadius: "8px", height: "38px", px: 3,
              background: "linear-gradient(180deg, #065F46 0%, #0D7B5C 100%)",
              color: "#fff", fontFamily: "Heebo", fontWeight: 600, textTransform: "none", fontSize: "13px",
              "&:hover": { opacity: 0.9, background: "linear-gradient(180deg, #065F46 0%, #0D7B5C 100%)" },
            }}
          >
            Confirm ({selectedIds.length})
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default NotificationModal;
